import React from 'react';
import type { BlockDefinition, TemplateModel, WebsitePage } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { canAddBlock, countBlockInstances } from './types';

interface PanelAddBlockProps {
  page: WebsitePage;
  model: TemplateModel;
  onAdd: (blockId: string) => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  hero: 'Destaque',
  content: 'Conteúdo',
  listing: 'Listagem',
  form: 'Formulário',
  navigation: 'Navegação',
  social: 'Social',
  media: 'Mídia',
  integration: 'Integração',
  footer: 'Rodapé',
};

/**
 * Mapeia os nomes de ícone do manifesto do template (PascalCase, ex: 'LayoutGrid')
 * para nomes válidos do IconName do frontend (kebab-case, ex: 'grid').
 * Ícones não mapeados caem em 'box' (sempre disponível).
 *
 * Valores reais nos templates (auditados em 2026-06-16; Video e Instagram
 * acrescentados em 2026-10-26 com os blocos featured-videos e instagram-feed):
 *   real-estate-premium: Image, Search, Heading, LayoutGrid, Megaphone,
 *     MessageSquareQuote, FileText, Users, TrendingUp, Award, Layers,
 *     BarChart, PanelBottom, Home, Building, Mail, Info, Video, Instagram
 *   blog-starter: BookOpen, Star, LayoutGrid, Tags, Mail, User,
 *     Megaphone, MessageSquareQuote, FileText, PanelBottom, Home,
 *     FileText, Info, Mail
 */
const TEMPLATE_ICON_MAP: Record<string, IconName> = {
  // Exact or case-normalized matches
  image: 'image',
  Image: 'image',
  search: 'search',
  Search: 'search',
  'file-text': 'file-text',
  FileText: 'file-text',
  users: 'users',
  Users: 'users',
  user: 'user',
  User: 'user',
  'trending-up': 'trending-up',
  TrendingUp: 'trending-up',
  layers: 'layers',
  Layers: 'layers',
  'bar-chart': 'bar-chart',
  BarChart: 'bar-chart',
  home: 'home',
  Home: 'home',
  mail: 'mail',
  Mail: 'mail',
  info: 'info',
  Info: 'info',
  star: 'star',
  Star: 'star',
  grid: 'grid',
  Video: 'video',
  Instagram: 'instagram',
  // Closest semantic matches for unmapped PascalCase icons
  LayoutGrid: 'grid',
  Heading: 'type',
  Megaphone: 'bell',
  MessageSquareQuote: 'message-square',
  Award: 'star',
  PanelBottom: 'layers',
  Building: 'briefcase',
  BookOpen: 'file-text',
  Tags: 'tag',
};

/** Converts a template icon name (string) to a valid IconName, falling back to 'box'. */
function toIconName(icon: string): IconName {
  return TEMPLATE_ICON_MAP[icon] ?? 'box';
}

/** Catálogo da paleta do template, agrupado por categoria. Itens no limite ficam desabilitados. */
export const PanelAddBlock: React.FC<PanelAddBlockProps> = ({ page, model, onAdd }) => {
  const byCategory = new Map<string, BlockDefinition[]>();
  for (const def of model.blocks) {
    const list = byCategory.get(def.category) ?? [];
    list.push(def);
    byCategory.set(def.category, list);
  }

  return (
    <div className="space-y-4" data-testid="panel-add-block">
      {[...byCategory.entries()].map(([category, defs]) => (
        <div key={category} className="space-y-1.5">
          <p className="text-[11px] font-medium uppercase tracking-wider px-1" style={{ color: 'var(--theme-muted-text)' }}>
            {CATEGORY_LABELS[category] || category}
          </p>
          {defs.map((def) => {
            const count = countBlockInstances(page, def.id);
            const enabled = canAddBlock(model, page, def.id);
            const limitLabel = def.maxInstances !== undefined ? `${count}/${def.maxInstances}` : null;
            return (
              <button
                key={def.id}
                type="button"
                disabled={!enabled}
                onClick={() => onAdd(def.id)}
                className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg border transition-colors text-left disabled:opacity-40"
                style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}
                data-testid={`add-block-item-${def.id}`}
              >
                <Icon name={toIconName(def.icon)} className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>{def.name}</span>
                  {def.description && (
                    <span className="block text-[11px] truncate" style={{ color: 'var(--theme-muted-text)' }}>{def.description}</span>
                  )}
                </span>
                {limitLabel && (
                  <span className="text-[11px] flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>{limitLabel}</span>
                )}
                {enabled
                  ? <Icon name="plus" className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
                  : <Icon name="check" className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
